import React from 'react';

import { Card, Recolhivel } from '../../../ui';

/**
 * "Regras do sistema" — diretriz de Victor (03/09/2026): **todas** as regras
 * vigentes dos contratos ficam listadas e visíveis NA TELA de administração, junto
 * dos conceitos acordados com o jurídico. Não é documentação externa: quem opera
 * precisa conseguir conferir a regra no lugar onde ela é aplicada.
 *
 * ⚠️ **Ao mudar uma regra no código, mudar o texto aqui na mesma entrega.** Uma
 * tela que descreve um comportamento que o sistema não tem mais é pior que tela
 * nenhuma. As 9 regras abaixo são **preservadas verbatim** (D8) — a at-69 mudou
 * só o envelope; há teste sentinela afirmando os 9 títulos.
 *
 * ## O que mudou na at-69
 *
 *   - **O acordeão virou o `Recolhivel`** (`<details>`/`<summary>` nativos): o
 *     `useState` + `aria-expanded` e o `+`/`−` como único marcador saíram. O
 *     pictograma sem texto contraria a regra de Victor de 06/09/2026; o chevron
 *     do kit gira e o `<summary>` anuncia "expandido" sozinho.
 *   - **O `<dl>` ficou correto**: havia um `<div>` entre ele e os `<dt>`/`<dd>`,
 *     o que quebra a associação termo/definição para o leitor de tela.
 */

interface Regra {
  titulo: string;
  texto: string;
}

const REGRAS_DO_DOCUMENTO: Regra[] = [
  {
    titulo: 'O que é o documento original',
    texto:
      'O original é o PDF arquivado, somado ao seu código de verificação (hash SHA-256). ' +
      'O sistema não regera o documento para conferir: ele guarda o arquivo exato que foi ' +
      'apresentado ao associado e o código que prova que aquele arquivo não mudou. Conceito ' +
      'acordado com o jurídico.',
  },
  {
    titulo: 'O que fica registrado na assinatura',
    texto:
      'O PDF assinado contém o contrato inteiro mais uma página final com o nome e o CPF de ' +
      'quem assinou, a data e a hora, o endereço de origem (IP) e o código de verificação do ' +
      'documento original.',
  },
  {
    titulo: 'Versão do texto',
    texto:
      'Cada contrato guarda a versão exata do modelo que o gerou. Editar o modelo depois não ' +
      'altera nenhum contrato já emitido — os antigos continuam apontando para o texto com que ' +
      'foram assinados.',
  },
  {
    titulo: 'Um contrato assina uma vez só',
    texto:
      'Depois de assinado, o mesmo contrato não pode ser assinado de novo. Se for preciso ' +
      'refazer, gere um contrato novo.',
  },
];

const REGRAS_DE_OPERACAO: Regra[] = [
  {
    titulo: 'A adesão conclui com contrato pendente',
    texto:
      'O consultor termina a adesão mesmo que o associado ainda não tenha assinado. O contrato ' +
      'pendente fica visível na carteira.',
  },
  {
    titulo: 'Prazo de pendência: 7 dias',
    texto:
      'Um contrato emitido e não assinado é considerado pendente por 7 dias. Não há lembrete ' +
      'automático — o reenvio do link é feito manualmente pelo consultor.',
  },
  {
    titulo: 'Acesso ao arquivo',
    texto:
      'O PDF não fica em endereço público. Consultor e TI abrem pelo sistema, com login; o ' +
      'associado recebe um link próprio, que expira em 7 dias e deixa de funcionar se o ' +
      'contrato for cancelado.',
  },
  {
    titulo: 'Distrato',
    texto:
      'Cancelar um contrato assinado é possível apenas para TI e Administrador, e exige um ' +
      'motivo, que fica registrado. Cancelar não apaga nada: o documento continua guardado.',
  },
  {
    titulo: 'Envio ao associado',
    texto:
      'Com o envio automático ligado, o contrato vai por WhatsApp pelo modelo de mensagem ' +
      'aprovado. Desligado ou indisponível, o consultor envia manualmente com o mesmo link — ' +
      'o contrato já está salvo de qualquer forma.',
  },
];

const TOTAL_DE_REGRAS = REGRAS_DO_DOCUMENTO.length + REGRAS_DE_OPERACAO.length;

const Secao: React.FC<{ titulo: string; regras: Regra[] }> = ({ titulo, regras }) => (
  <section className="tw-flex tw-flex-col tw-gap-2">
    <h3 className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-texto-suave">
      {titulo}
    </h3>

    {/* ⚠️ `<dt>`/`<dd>` como filhos DIRETOS do `<dl>` — havia um `<div>` no meio,
        o que quebra a associação termo/definição para o leitor de tela. */}
    <dl className="tw-m-0 tw-flex tw-flex-col tw-gap-3">
      {regras.map((regra) => (
        <React.Fragment key={regra.titulo}>
          <dt className="tw-text-sm tw-font-semibold tw-text-texto-titulo">{regra.titulo}</dt>
          <dd className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-texto-corpo">
            {regra.texto}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  </section>
);

const RegrasDeContrato: React.FC = () => (
  <Card aparencia="nenhuma">
    <Recolhivel titulo="Regras do sistema" resumo={`${TOTAL_DE_REGRAS} regras`}>
      <div className="tw-flex tw-flex-col tw-gap-5">
        <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
          Como o sistema trata os contratos hoje. Estas regras valem para todos os termos gerados
          pelo SAAC.
        </p>

        <Secao titulo="O documento" regras={REGRAS_DO_DOCUMENTO} />
        <Secao titulo="Operação" regras={REGRAS_DE_OPERACAO} />
      </div>
    </Recolhivel>
  </Card>
);

export default RegrasDeContrato;
